// Cache da "Explicação da IA" por QUESTÃO em modo Revisão de prova
// encerrada. Chaveada por (bankId, provaId, qid) — escopo de uma prova
// específica pra não vazar explicação entre tentativas distintas do mesmo
// banco. Mesma estratégia do aiAnalysisCache: localStorage primário + server
// como fonte cross-device.
//
// Diferença pro AiAnalysisCache: aqui guardamos POR QUESTÃO, então o map é
// duplo: localStorage usa chave `ai-explain-${bankId}-${provaId}-${qid}`.
// Server endpoint reúsa estrutura existente em bank_progress.data._aiExplain.

export interface AiExplainCache {
  text: string;
  generatedAt: string;
}

export function aiExplainStorageKey(
  bankId: string,
  provaId: string,
  qid: string
): string {
  return `ai-explain-${bankId}-${provaId}-${qid}`;
}

export function readAiExplainCache(
  bankId: string,
  provaId: string,
  qid: string
): AiExplainCache | null {
  try {
    const raw = localStorage.getItem(aiExplainStorageKey(bankId, provaId, qid));
    if (!raw) return null;
    return JSON.parse(raw) as AiExplainCache;
  } catch {
    return null;
  }
}

export function writeAiExplainCache(
  bankId: string,
  provaId: string,
  qid: string,
  cache: AiExplainCache
): void {
  try {
    localStorage.setItem(
      aiExplainStorageKey(bankId, provaId, qid),
      JSON.stringify(cache)
    );
  } catch {
    /* quota / private mode */
  }
}

export function deleteAiExplainCache(
  bankId: string,
  provaId: string,
  qid: string
): void {
  try {
    localStorage.removeItem(aiExplainStorageKey(bankId, provaId, qid));
  } catch {
    /* ignore */
  }
}

// ============================================================================
// Sync com server — opt-in. Mesma filosofia do aiAnalysisCache: falha
// silenciosa, localStorage continua sendo fonte rápida. Server cobre
// cross-device.
// ============================================================================

export async function fetchAiExplainFromServer(
  bankId: string,
  provaId: string,
  qid: string
): Promise<AiExplainCache | null> {
  try {
    const url = `/api/student/ai-explain?bankId=${encodeURIComponent(
      bankId
    )}&provaId=${encodeURIComponent(provaId)}&qid=${encodeURIComponent(qid)}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      explanation: { text: string; generatedAt: string } | null;
    };
    if (!data.explanation) return null;
    return {
      text: data.explanation.text,
      generatedAt: data.explanation.generatedAt
    };
  } catch {
    return null;
  }
}

export async function saveAiExplainToServer(
  bankId: string,
  provaId: string,
  qid: string,
  cache: AiExplainCache,
  model?: string | null
): Promise<void> {
  try {
    await fetch("/api/student/ai-explain", {
      method: "PUT",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        bankId,
        provaId,
        qid,
        text: cache.text,
        model
      })
    });
  } catch {
    /* ignore — localStorage já tem */
  }
}

export async function deleteAiExplainFromServer(
  bankId: string,
  provaId: string,
  qid: string
): Promise<void> {
  try {
    await fetch(
      `/api/student/ai-explain?bankId=${encodeURIComponent(
        bankId
      )}&provaId=${encodeURIComponent(provaId)}&qid=${encodeURIComponent(qid)}`,
      { method: "DELETE", credentials: "include" }
    );
  } catch {
    /* ignore */
  }
}
